import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../lib/api';
import { requireAccess } from '../lib/guards';
import { PageHeader } from '../components/ui/PageHeader';
import { Select } from '../components/ui/Select';
import { type BvStatus, type EntwurfStatus } from '../lib/api-enums';
import {
  BV_STATUS_BADGE,
  BV_STATUS_LABELS,
  ENTWURF_STATUS_BADGE,
  ENTWURF_STATUS_LABELS,
} from '../lib/api-labels';
import { formatGermanDateFromTimestamp } from '../lib/date-format';

interface Entwurf {
  id: string;
  kundeId: string;
  objektId: string;
  status: EntwurfStatus;
  notizen: string | null;
  createdAt: string;
}

interface Bv {
  id: string;
  bvNummer: string | null;
  kundeId: string;
  objektId: string;
  status: BvStatus;
  laufzeitVon: string;
  monatsbetragNetto: string;
  quartalsbetragNetto: string;
  gekuendigtAm: string | null;
  beendetZum: string | null;
  pauseVon: string | null;
  pauseBis: string | null;
}

interface Kunde {
  id: string;
  kundennummer: string;
  firmenname: string;
}

interface Objekt {
  id: string;
  bezeichnung: string;
}

export const Route = createFileRoute('/vertraege/')({
  beforeLoad: () => requireAccess('/api/v1/vertraege/entwuerfe'),
  component: VertraegeListPage,
});

function VertraegeListPage() {
  const [tab, setTab] = useState<'entwurf' | 'bv'>('entwurf');
  const [statusFilterEntwurf, setStatusFilterEntwurf] = useState<EntwurfStatus | ''>('');
  const [statusFilterBv, setStatusFilterBv] = useState<BvStatus | ''>('');

  const entwuerfeQ = useQuery({
    queryKey: ['vertraege', 'entwuerfe', statusFilterEntwurf || 'all'],
    queryFn: () =>
      api<{ items: Entwurf[] }>(
        statusFilterEntwurf
          ? `/api/v1/vertraege/entwuerfe?status=${statusFilterEntwurf}`
          : '/api/v1/vertraege/entwuerfe',
      ),
    enabled: tab === 'entwurf',
  });

  const bvsQ = useQuery({
    queryKey: ['vertraege', 'bvs', statusFilterBv || 'all'],
    queryFn: () =>
      api<{ items: Bv[] }>(
        statusFilterBv ? `/api/v1/vertraege/bv?status=${statusFilterBv}` : '/api/v1/vertraege/bv',
      ),
    enabled: tab === 'bv',
  });

  const kundenQ = useQuery({
    queryKey: ['kunden', 'lookup'],
    queryFn: () => api<{ items: Kunde[] }>('/api/v1/kunden'),
  });
  const kundenById = new Map((kundenQ.data?.items ?? []).map((k) => [k.id, k]));

  const objekteQ = useQuery({
    queryKey: ['objekte', 'lookup'],
    queryFn: () => api<{ items: Objekt[] }>('/api/v1/objekte'),
  });
  const objekteById = new Map((objekteQ.data?.items ?? []).map((o) => [o.id, o]));

  const entwuerfe = entwuerfeQ.data?.items ?? [];
  const bvs = bvsQ.data?.items ?? [];

  const entwurfCounts = entwuerfe.reduce<Record<string, number>>((acc, e) => {
    acc[e.status] = (acc[e.status] ?? 0) + 1;
    return acc;
  }, {});
  const bvCounts = bvs.reduce<Record<string, number>>((acc, b) => {
    acc[b.status] = (acc[b.status] ?? 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      <PageHeader
        title="Verträge"
        description="Vertragsentwürfe (ENT) und aktive Betreuungsverträge (BV) inkl. Lifecycle."
        actions={
          <div className="flex items-center gap-2">
            <Link
              to="/vertraege/neu/vorlagen"
              className="inline-flex items-center rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700"
            >
              + Neuer Entwurf aus Vorlagen
            </Link>
            <Link
              to="/vertraege/neu"
              className="inline-flex items-center rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              + Freier Entwurf
            </Link>
          </div>
        }
      />

      <div className="border-b border-slate-200">
        <nav className="flex gap-6 text-sm">
          <button
            onClick={() => setTab('entwurf')}
            className={
              tab === 'entwurf'
                ? 'border-b-2 border-brand-600 px-1 py-2 font-medium text-brand-700'
                : 'px-1 py-2 text-slate-500 hover:text-slate-700'
            }
          >
            Entwürfe ({entwuerfe.length})
          </button>
          <button
            onClick={() => setTab('bv')}
            className={
              tab === 'bv'
                ? 'border-b-2 border-brand-600 px-1 py-2 font-medium text-brand-700'
                : 'px-1 py-2 text-slate-500 hover:text-slate-700'
            }
          >
            Betreuungsverträge ({bvs.length})
          </button>
        </nav>
      </div>

      {tab === 'entwurf' ? (
        <>
          <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <span className="font-medium text-slate-700">Filter:</span>
              <Select
                value={statusFilterEntwurf}
                onChange={(e) => setStatusFilterEntwurf(e.target.value as EntwurfStatus | '')}
                className="w-44"
              >
                <option value="">Alle Status</option>
                {(Object.keys(ENTWURF_STATUS_LABELS) as EntwurfStatus[]).map((s) => (
                  <option key={s} value={s}>
                    {ENTWURF_STATUS_LABELS[s]} ({entwurfCounts[s] ?? 0})
                  </option>
                ))}
              </Select>
            </div>
          </div>

          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr className="text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                  <th className="px-3 py-2">Erstellt</th>
                  <th className="px-3 py-2">Kunde</th>
                  <th className="px-3 py-2">Objekt</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Notizen</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 bg-white text-sm">
                {entwuerfe.map((e) => (
                  <tr
                    key={e.id}
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => {
                      window.location.href = `/vertraege/entwurf/${e.id}`;
                    }}
                  >
                    <td className="whitespace-nowrap px-3 py-2 text-slate-700">
                      {formatGermanDateFromTimestamp(e.createdAt)}
                    </td>
                    <td className="px-3 py-2 text-slate-700">
                      {kundenById.get(e.kundeId)?.firmenname ?? e.kundeId.slice(0, 8)}
                    </td>
                    <td className="px-3 py-2 text-slate-700">
                      {objekteById.get(e.objektId)?.bezeichnung ?? e.objektId.slice(0, 8)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2">
                      <span
                        className={`inline-flex rounded-md border px-2 py-0.5 text-xs font-medium ${ENTWURF_STATUS_BADGE[e.status]}`}
                      >
                        {ENTWURF_STATUS_LABELS[e.status]}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-slate-600">
                      <span className="line-clamp-1">{e.notizen ?? '—'}</span>
                    </td>
                  </tr>
                ))}
                {entwuerfe.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-3 py-8 text-center text-sm text-slate-500">
                      Keine Entwürfe — neuen Entwurf anlegen.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <>
          <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <span className="font-medium text-slate-700">Filter:</span>
              <Select
                value={statusFilterBv}
                onChange={(e) => setStatusFilterBv(e.target.value as BvStatus | '')}
                className="w-56"
              >
                <option value="">Alle Status</option>
                {(Object.keys(BV_STATUS_LABELS) as BvStatus[]).map((s) => (
                  <option key={s} value={s}>
                    {BV_STATUS_LABELS[s]} ({bvCounts[s] ?? 0})
                  </option>
                ))}
              </Select>
            </div>
          </div>

          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr className="text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                  <th className="px-3 py-2">BV-Nr</th>
                  <th className="px-3 py-2">Kunde</th>
                  <th className="px-3 py-2">Objekt</th>
                  <th className="px-3 py-2">Laufzeit ab</th>
                  <th className="px-3 py-2 text-right">Quartalsbetrag</th>
                  <th className="px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 bg-white text-sm">
                {bvs.map((b) => (
                  <tr
                    key={b.id}
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => {
                      window.location.href = `/vertraege/bv/${b.id}`;
                    }}
                  >
                    <td className="whitespace-nowrap px-3 py-2 font-mono text-slate-700">
                      {b.bvNummer ?? '—'}
                    </td>
                    <td className="px-3 py-2 text-slate-700">
                      {kundenById.get(b.kundeId)?.firmenname ?? b.kundeId.slice(0, 8)}
                    </td>
                    <td className="px-3 py-2 text-slate-700">
                      {objekteById.get(b.objektId)?.bezeichnung ?? b.objektId.slice(0, 8)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-slate-700">{b.laufzeitVon}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-right font-mono text-slate-700">
                      {b.quartalsbetragNetto} €
                    </td>
                    <td className="whitespace-nowrap px-3 py-2">
                      <span
                        className={`inline-flex rounded-md border px-2 py-0.5 text-xs font-medium ${BV_STATUS_BADGE[b.status]}`}
                      >
                        {BV_STATUS_LABELS[b.status]}
                      </span>
                    </td>
                  </tr>
                ))}
                {bvs.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-3 py-8 text-center text-sm text-slate-500">
                      Keine BVs — Entwurf akzeptieren + aktivieren, um einen BV zu erzeugen.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
